/**
 * Octane quiz results renderer.
 *
 * Listens for Octane's `octane.quiz.completed` event, then rebuilds the results page using theme-native
 * product cards (fetched through the Section Rendering API, section: `quiz-result-card`) while preserving
 * Octane's content-block structure: each block's `top_content` -> products -> `bottom_content` are rendered
 * in order, so upsell / descriptive copy between product groups is kept.
 *
 * Requires: in Octane, set Results Page Behavior to "Octane AI will NOT create your results pages".
 */
(function () {
  'use strict';

  var CARD_SECTION_DEFAULT = 'quiz-result-card';

  function init(root) {
    if (!root || root.dataset.octaneReady === 'true') return;
    root.dataset.octaneReady = 'true';

    var refs = {
      root: root,
      cardSection: root.dataset.cardSection || CARD_SECTION_DEFAULT,
      blocksEl: root.querySelector('[data-quiz-blocks]'),
      messageEl: root.querySelector('[data-quiz-message]'),
      loadingEl: root.querySelector('[data-quiz-loading]'),
      retakeEl: root.querySelector('[data-quiz-retake]'),
      retakeUrl: root.dataset.retakeUrl || ''
    };

    if (refs.retakeEl) {
      refs.retakeEl.addEventListener('click', function (event) {
        event.preventDefault();
        restartQuiz(refs.retakeUrl);
      });
    }

    document.addEventListener(
      'octane.quiz.completed',
      function (event) {
        handleCompleted(event, refs);
      },
      false
    );
  }

  function handleCompleted(event, refs) {
    var detail = (event && event.detail) || {};

    // Keep this while we validate the exact payload shape on the store (content field names, visibility flags).
    console.log('[octane-quiz-results] octane.quiz.completed', detail);

    hideOctaneResults(detail);

    var message = detail.ai_personalized_message || detail.ai_personalization_message;
    if (message && refs.messageEl) {
      refs.messageEl.innerHTML = '<p>' + escapeHtml(message) + '</p>';
      refs.messageEl.hidden = false;
    } else if (refs.messageEl && refs.messageEl.innerHTML.trim() !== '') {
      refs.messageEl.hidden = false;
    }

    refs.root.hidden = false;

    var blocks = Array.isArray(detail.sections) ? detail.sections : [];

    renderBlocks(blocks, refs)
      .then(function () {
        finish(refs);
        refs.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      })
      .catch(function (error) {
        console.error('[octane-quiz-results] Failed to render results', error);
        finish(refs);
      });
  }

  function finish(refs) {
    if (refs.loadingEl) refs.loadingEl.hidden = true;
    if (refs.blocksEl) refs.blocksEl.setAttribute('aria-busy', 'false');
  }

  /**
   * Render every Octane content block in order. For each block we render its top content,
   * a centred grid of theme-native product cards, then its bottom content.
   */
  function renderBlocks(blocks, refs) {
    if (!refs.blocksEl) return Promise.resolve();

    var jobs = blocks.map(function (block) {
      return buildBlock(block, refs);
    });

    return Promise.all(jobs).then(function (blockEls) {
      var fragment = document.createDocumentFragment();
      blockEls.forEach(function (el) {
        if (el) fragment.appendChild(el);
      });
      refs.blocksEl.appendChild(fragment);
    });
  }

  function buildBlock(block, refs) {
    if (!block) return Promise.resolve(null);

    var products = normaliseProducts(block.products);
    var topContent = renderContentItems(block.top_content);
    var bottomContent = renderContentItems(block.bottom_content);

    // Skip blocks that would render nothing at all.
    if (!products.length && !topContent && !bottomContent) {
      return Promise.resolve(null);
    }

    return fetchCards(products, refs.cardSection).then(function (cardEls) {
      var blockEl = document.createElement('section');
      blockEl.className = 'quiz-results__block';
      if (block.id) blockEl.dataset.blockId = block.id;

      if (topContent) {
        var top = document.createElement('div');
        top.className = 'quiz-results__copy rte';
        top.innerHTML = topContent;
        blockEl.appendChild(top);
      }

      if (cardEls.length) {
        // Match the theme's Best Sellers grid: a scroll-area carousel wrapping a product-list.
        var carousel = document.createElement('div');
        carousel.className = 'quiz-results__carousel product-card-carousel scroll-area bleed md:unbleed';

        var list = document.createElement('product-list');
        list.className = 'product-list';
        list.setAttribute('role', 'list');

        cardEls.forEach(function (card) {
          list.appendChild(card);
        });

        carousel.appendChild(list);
        blockEl.appendChild(carousel);
      }

      if (bottomContent) {
        var bottom = document.createElement('div');
        bottom.className = 'quiz-results__copy rte';
        bottom.innerHTML = bottomContent;
        blockEl.appendChild(bottom);
      }

      return blockEl;
    });
  }

  function normaliseProducts(products) {
    if (!Array.isArray(products)) return [];
    var seen = {};
    var out = [];
    products.forEach(function (product) {
      var link = product && product.link;
      var handle = handleFromLink(link);
      if (!handle || seen[handle]) return;
      seen[handle] = true;
      out.push({ handle: handle, variantId: variantFromLink(link) });
    });
    return out;
  }

  function handleFromLink(link) {
    if (!link || typeof link !== 'string') return null;
    var match = link.split('/products/')[1];
    if (!match) return null;
    return match.split('?')[0].split('#')[0].replace(/\/$/, '');
  }

  /**
   * Only honour a variant that Octane explicitly specified (e.g. a chosen kids size), which shows up
   * as `?variant=<id>` in the product link. General product recommendations have no variant in the
   * link, so we return null and let the theme default to the first available (in-stock) variant.
   */
  function variantFromLink(link) {
    if (!link || typeof link !== 'string') return null;
    var query = link.split('?')[1];
    if (!query) return null;
    var params = query.split('#')[0].split('&');
    for (var i = 0; i < params.length; i++) {
      var pair = params[i].split('=');
      if (pair[0] === 'variant' && pair[1]) return pair[1];
    }
    return null;
  }

  function fetchCards(products, cardSection) {
    if (!products.length) return Promise.resolve([]);

    var requests = products.map(function (product) {
      return fetchCard(product, cardSection);
    });

    return Promise.all(requests).then(function (cards) {
      return cards.filter(Boolean);
    });
  }

  /**
   * Fetch a single product card via the Section Rendering API and return the bare <product-card>
   * element so it can sit as a direct child of a theme `product-list` (needed for the subgrid /
   * equal-height card behaviour used by the Best Sellers grid).
   *
   * If Octane specified a variant (via `?variant=` in the product link) we honour it, even if it's
   * out of stock. Otherwise we pass no variant so the theme defaults to
   * `selected_or_first_available_variant` (first in-stock), matching the rest of the site and
   * avoiding an unintended sold-out variant pre-selection.
   */
  function fetchCard(product, cardSection) {
    var url =
      '/products/' +
      encodeURIComponent(product.handle) +
      '?section_id=' +
      encodeURIComponent(cardSection);

    if (product.variantId) {
      url += '&variant=' + encodeURIComponent(product.variantId);
    }

    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status + ' for ' + url);
        return response.text();
      })
      .then(function (html) {
        var template = document.createElement('template');
        template.innerHTML = html.trim();
        return template.content.querySelector('product-card');
      })
      .catch(function (error) {
        console.error('[octane-quiz-results] Could not load card for ' + product.handle, error);
        return null;
      });
  }

  /**
   * Render an array of Octane content items (title / body / html / image / button) into an HTML string.
   * Field names for non-text content are best-effort until confirmed against the live payload.
   */
  function renderContentItems(items) {
    if (!Array.isArray(items) || !items.length) return '';

    return items
      .map(function (item) {
        if (!item) return '';
        var type = (item.content_type || '').toLowerCase();

        if (type === 'html') {
          return item.html || item.text || '';
        }

        if (type === 'image') {
          var src = item.image || item.src || item.url;
          if (!src) return '';
          var alt = escapeHtml(item.alt || item.alt_text || '');
          return '<img class="quiz-results__image" src="' + escapeAttr(src) + '" alt="' + alt + '" loading="lazy">';
        }

        if (type === 'button' || type === 'link') {
          var href = item.url || item.link || item.href;
          var label = escapeHtml(item.text || item.label || 'Learn more');
          if (!href) return '';
          return '<a class="button button--secondary" href="' + escapeAttr(href) + '">' + label + '</a>';
        }

        // Default: text content. Use text_type to decide heading vs paragraph.
        var textType = (item.text_type || '').toLowerCase();
        var text = item.text || '';
        if (!text) return '';

        if (textType === 'title') {
          return '<h2 class="h3">' + escapeHtml(text) + '</h2>';
        }
        if (textType === 'subtitle') {
          return '<p class="h6">' + escapeHtml(text) + '</p>';
        }
        // body_text or unspecified: rebuild real paragraphs / lists from Octane's plain text.
        return '<div class="quiz-results__body">' + formatBody(text) + '</div>';
      })
      .join('');
  }

  /**
   * Octane sends body copy as plain text: bullet lines start with a "•" (or -, *) and blocks are
   * separated by newlines. Inserted as-is those line breaks collapse, so we rebuild real <p> / <ul>
   * markup here. Bullet lines get their leading product name emphasised (bold).
   *
   * If Octane ever sends real list markup, or uses <br> tags, we handle those too.
   */
  function formatBody(text) {
    var raw = String(text == null ? '' : text);
    if (!raw.trim()) return '';

    // Trust Octane if it already sent list markup.
    if (/<(ul|ol|li)\b/i.test(raw)) return raw;

    var normalised = raw.replace(/<br\s*\/?>/gi, '\n');
    var lines = normalised.split('\n');
    var out = '';
    var items = [];

    function flushList() {
      if (!items.length) return;
      out += '<ul class="quiz-results__list">' + items.join('') + '</ul>';
      items = [];
    }

    lines.forEach(function (line) {
      var trimmed = line.trim();
      if (!trimmed) {
        flushList();
        return;
      }

      var bullet = trimmed.match(/^[•\u2022\-\*‣◦·]\s+(.*)$/);
      if (bullet) {
        items.push('<li>' + emphasiseLeadName(escapeHtml(bullet[1])) + '</li>');
      } else {
        flushList();
        out += '<p>' + escapeHtml(trimmed) + '</p>';
      }
    });
    flushList();

    return out || '<p>' + escapeHtml(raw) + '</p>';
  }

  /**
   * Bold the leading product name in a bullet, e.g. "✈️ Alpine Flyfit Earplugs help…" ->
   * "✈️ <strong>Alpine Flyfit Earplugs</strong> help…". We skip any leading emoji/symbol, then wrap
   * the leading run of Title-Case / numeric words (the product name) up to the first lower-case word.
   * Operates on already-escaped text.
   */
  function emphasiseLeadName(escapedText) {
    return escapedText.replace(
      /^(\s*(?:[^A-Za-z0-9<]+\s*)?)([A-Z0-9][^\s]*(?:\s+[A-Z0-9][^\s]*)*)/,
      function (match, lead, name) {
        var trail = '';
        var punctuation = name.match(/[).,;:!?]+$/);
        if (punctuation) {
          trail = punctuation[0];
          name = name.slice(0, name.length - trail.length);
        }
        if (!name) return match;
        return lead + '<strong>' + name + '</strong>' + trail;
      }
    );
  }

  /**
   * Octane has no JS reset API; the supported "restart" is to (re)load the quiz page URL.
   * We strip any results hash so the quiz starts fresh rather than reopening the results.
   */
  function restartQuiz(retakeUrl) {
    var target = retakeUrl || window.location.pathname + window.location.search;

    // If the target is the current page, drop the results hash before reloading.
    var isSamePage = target === window.location.pathname || target === window.location.pathname + window.location.search;

    if (isSamePage) {
      if (window.location.hash) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      window.location.reload();
    } else {
      window.location.href = target;
    }
  }

  /**
   * Collapse Octane's own quiz/results markup so only the theme-native results show.
   *
   * Notes:
   * - Once Octane initialises, it renames the mount class from `octane-ai-quiz` to
   *   `octane-ai-quiz-rendered`, so we match both (via [class*="octane-ai-quiz"]).
   * - The mount is sized to the viewport (data-*-height="match_browser", min-height ~640px+), so we
   *   hide the whole embed section (closest .shopify-section) to remove the tall blank gap entirely.
   */
  function hideOctaneResults(detail) {
    var targets = [];

    var wrapper = detail && detail.quiz_wrapper_element;
    if (wrapper && wrapper.nodeType === 1) targets.push(wrapper);

    document.querySelectorAll('[class*="octane-ai-quiz"]').forEach(function (el) {
      targets.push(el);
    });

    targets.forEach(function (el) {
      var section = el.closest ? el.closest('.shopify-section') : null;
      (section || el).style.display = 'none';
    });
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = String(value == null ? '' : value);
    return div.innerHTML;
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/"/g, '&quot;');
  }

  function boot() {
    document.querySelectorAll('quiz-results').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
